/**
 * 사진 고르기 — 브라우저에서 줄인 다음 올린다.
 *
 * 원본을 그대로 올리지 않는 이유가 셋이다:
 * - 요즘 폰 사진은 한 장에 5~10MB다. 격자 한 칸이 150px인데 4000px을 올릴
 *   이유가 없고, 보는 사람이 그걸 전부 내려받게 된다(전송량 = 돈).
 * - 서버에서 줄이려면 이미지 처리 런타임이 필요하다. "0원에 가까운 인프라"와
 *   맞지 않는다. 캔버스는 사용자 기기에서 공짜로 돈다.
 * - EXIF가 통째로 빠진다. 사진에 박힌 **GPS 좌표를 공개 프로필에 올리는**
 *   일이 없어진다.
 */

import {
  MAX_IMAGE_BYTES,
  UPLOADABLE_IMAGE_TYPES,
  type UploadableImageType,
} from '@lifetimeline/shared';

/** 긴 변 기준 상한. 격자(150px)와 상세 보기(480px)에 2배 밀도까지 충분하다 */
const MAX_EDGE = 1280;
const QUALITY = 0.85;
const OUTPUT_TYPE: UploadableImageType = 'image/jpeg';

export type ImagePickError =
  /** 이미지 파일이 아님 */
  | 'type'
  /** 줄여도 한도를 넘음 */
  | 'tooLarge'
  /** 파일이 깨져서 못 읽음 */
  | 'decode';

export class ImageFileError extends Error {
  constructor(readonly reason: ImagePickError) {
    super(reason);
  }
}

/** 브라우저가 그릴 수 있는 형식인가. HEIC 등을 여기서 먼저 거른다 */
export function isSupportedInput(file: File): boolean {
  return file.type.startsWith('image/');
}

/**
 * 고른 파일을 올릴 수 있는 형태로 바꾼다.
 *
 * 결과는 항상 jpeg다 — 형식을 하나로 고정하면 확장자·MIME·정책을 맞출 곳이
 * 한 군데로 줄고, png 스크린샷이 통째로 올라가는 일도 없어진다.
 */
export async function prepareImage(file: File): Promise<Blob> {
  if (!isSupportedInput(file)) {
    throw new ImageFileError('type');
  }

  const bitmap = await decode(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.max(1, Math.round(bitmap.width * scale));
  const height = Math.max(1, Math.round(bitmap.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (context === null) {
    throw new ImageFileError('decode');
  }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, OUTPUT_TYPE, QUALITY);
  });
  if (blob === null) {
    throw new ImageFileError('decode');
  }
  if (blob.size > MAX_IMAGE_BYTES) {
    throw new ImageFileError('tooLarge');
  }
  return blob;
}

/**
 * 잘라 낸 자리만 그려 낸다.
 *
 * `prepareImage`가 사진 전체를 줄이는 것과 다르다 — 여기서는 **보이던 자리**
 * 만 남긴다. 화면에서 본 것과 같아야 하므로 잘라 낼 자리는 `cropGeometry`가
 * 계산한 값을 그대로 받는다.
 *
 * 결과는 여기서도 jpeg다. 잘라 냈으니 투명한 자리가 있을 수 없고, 형식이
 * 하나면 확장자·MIME·정책을 맞출 곳이 한 군데로 줄어든다.
 */
export async function renderCrop(
  bitmap: ImageBitmap,
  source: { x: number; y: number; width: number; height: number },
  output: { width: number; height: number },
): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = output.width;
  canvas.height = output.height;
  const context = canvas.getContext('2d');
  if (context === null) {
    throw new ImageFileError('decode');
  }
  context.drawImage(
    bitmap,
    source.x,
    source.y,
    source.width,
    source.height,
    0,
    0,
    output.width,
    output.height,
  );

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, OUTPUT_TYPE, QUALITY);
  });
  if (blob === null) {
    throw new ImageFileError('decode');
  }
  if (blob.size > MAX_IMAGE_BYTES) {
    throw new ImageFileError('tooLarge');
  }
  return blob;
}

/** 자르기 화면이 쓴다 — 파일을 비트맵으로 읽어 그대로 돌려준다 */
export async function decodeImage(file: File): Promise<ImageBitmap> {
  if (!isSupportedInput(file)) {
    throw new ImageFileError('type');
  }
  return decode(file);
}

/** 긴 변 상한. 자르기 화면도 같은 값을 쓴다 */
export const MAX_OUTPUT_EDGE = MAX_EDGE;

/**
 * 파일을 비트맵으로 읽는다.
 *
 * `imageOrientation: 'from-image'`가 핵심이다 — 없으면 세로로 찍은 폰 사진이
 * 캔버스에서 눕는다(EXIF 회전 정보를 캔버스가 자동으로 적용하지 않는다).
 * 이 옵션을 모르는 브라우저를 위해 `<img>` 경로로 한 번 더 시도한다.
 */
async function decode(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    try {
      return await createImageBitmap(file);
    } catch {
      throw new ImageFileError('decode');
    }
  }
}

export function outputType(): UploadableImageType {
  return OUTPUT_TYPE;
}

/** `<input type="file">`의 accept 값 */
export const ACCEPT_ATTRIBUTE = UPLOADABLE_IMAGE_TYPES.join(',');
